/**
 * Semantic analysis.
 *
 * Runs after a successful (or partially recovered) parse and answers the
 * questions the parser cannot: does this function exist, is this `break` inside
 * a loop, can this statement ever run, does this call chain loop back on itself.
 *
 * It also produces the symbol tables the rest of the pipeline needs — the
 * function map for the graph compiler and the editor's autocomplete, and the
 * call graph for recursion-safe expansion.
 */
import type { Block, FunctionDeclaration, Program, Statement, SwitchCase } from '../ast/nodes';
import { collectFunctions, mainFlow } from '../ast/nodes';
import type { SourceRange } from '../ast/source';
import type { Diagnostic } from '../diagnostics/types';
import { DiagnosticCode, dedupeDiagnostics, sortDiagnostics } from '../diagnostics/types';

export interface CallSite {
  readonly callee: string;
  readonly range: SourceRange;
  /** Normalized name of the enclosing function, or `null` at top level. */
  readonly caller: string | null;
  readonly statementId: string;
}

export interface SemanticInfo {
  /** Normalized function name -> declaration. First declaration wins on a clash. */
  readonly functions: ReadonlyMap<string, FunctionDeclaration>;
  /** Normalized caller -> normalized callees. Top-level flow is keyed `''`. */
  readonly callGraph: ReadonlyMap<string, ReadonlySet<string>>;
  readonly callSites: readonly CallSite[];
  /** Functions that participate in a call cycle, direct or indirect. */
  readonly recursiveFunctions: ReadonlySet<string>;
  /** Functions reachable from the main flow. */
  readonly reachableFunctions: ReadonlySet<string>;
  /** Explicit `@id(...)` values -> where they were declared. */
  readonly explicitIds: ReadonlyMap<string, SourceRange>;
  readonly diagnostics: readonly Diagnostic[];
}

/** Where a statement sits, which is what makes `break`/`continue` legal or not. */
export interface Context {
  readonly loopDepth: number;
  readonly switchDepth: number;
  readonly functionName: string | null;
}

const TOP_LEVEL: Context = { loopDepth: 0, switchDepth: 0, functionName: null };

export function analyze(program: Program): SemanticInfo {
  const diagnostics: Diagnostic[] = [];
  const functions = new Map<string, FunctionDeclaration>();
  const callGraph = new Map<string, Set<string>>();
  const callSites: CallSite[] = [];
  const explicitIds = new Map<string, SourceRange>();

  /* --- 1. Build the function table ---------------------------------- */

  for (const declaration of collectFunctions(program)) {
    const existing = functions.get(declaration.normalizedName);
    if (existing) {
      diagnostics.push({
        code: DiagnosticCode.DuplicateFunction,
        severity: 'error',
        message: `A function named "${declaration.name.text}" is already declared.`,
        range: declaration.name.range,
        suggestion: 'Rename one of them — function names must be unique.',
        related: [{ message: 'First declared here.', range: existing.name.range }],
        params: {
          name: declaration.name.text,
          line: existing.name.range.start.line,
        },
      });
      continue;
    }
    functions.set(declaration.normalizedName, declaration);
    callGraph.set(declaration.normalizedName, new Set());
  }
  callGraph.set('', new Set());

  /* --- 2. Walk every statement -------------------------------------- */

  const visitBlock = (block: Block, context: Context): void => {
    if (block.statements.length === 0) {
      diagnostics.push({
        code: DiagnosticCode.EmptyBlock,
        severity: 'info',
        message: 'This block is empty.',
        range: block.range,
        suggestion: 'Add a statement, or remove the block.',
      });
      return;
    }
    visitStatements(block.statements, context);
  };

  const visitStatements = (statements: readonly Statement[], context: Context): void => {
    let terminator: Statement | null = null;

    for (const statement of statements) {
      if (terminator) {
        diagnostics.push(unreachable(statement, terminator));
        // Keep analysing so genuine problems deeper in the dead code still
        // surface — the user is probably about to move this text, not delete it.
      }
      visitStatement(statement, context);
      if (!terminator && alwaysTerminates(statement, context)) {
        terminator = statement;
      }
    }
  };

  const visitStatement = (statement: Statement, context: Context): void => {
    recordExplicitId(statement.metadata.id, statement.range);

    switch (statement.kind) {
      case 'CallStatement': {
        const normalized = statement.callee.text.toLowerCase();
        callSites.push({
          callee: normalized,
          range: statement.callee.range,
          caller: context.functionName,
          statementId: statement.stableId,
        });
        const key = context.functionName ?? '';
        const edges = callGraph.get(key);
        if (edges) edges.add(normalized);
        else callGraph.set(key, new Set([normalized]));

        if (!functions.has(normalized)) {
          diagnostics.push({
            code: DiagnosticCode.UnknownFunction,
            severity: 'error',
            message: `No function named "${statement.callee.text}" is declared.`,
            range: statement.callee.range,
            suggestion: suggestFunctionName(normalized, functions),
            params: { name: statement.callee.text },
          });
        }
        break;
      }

      case 'BreakStatement':
        if (context.loopDepth === 0 && context.switchDepth === 0) {
          diagnostics.push({
            code: DiagnosticCode.BreakOutsideLoop,
            severity: 'error',
            message: '`break` is only allowed inside a loop or a switch.',
            range: statement.range,
            suggestion: 'Remove it, or put it inside `while`, `repeat`, `for each` or `switch`.',
          });
        }
        break;

      case 'ContinueStatement':
        if (context.loopDepth === 0) {
          diagnostics.push({
            code: DiagnosticCode.ContinueOutsideLoop,
            severity: 'error',
            message: '`continue` is only allowed inside a loop.',
            range: statement.range,
            suggestion: 'Remove it, or put it inside `while`, `repeat` or `for each`.',
          });
        }
        break;

      case 'ReturnStatement':
        if (context.functionName === null) {
          diagnostics.push({
            code: DiagnosticCode.ReturnOutsideFunction,
            severity: 'warning',
            message: '`return` outside a function ends the whole flow.',
            range: statement.range,
            suggestion: 'Use `end;` if you meant to finish the diagram.',
          });
        }
        break;

      case 'IfStatement': {
        if (statement.condition.text.trim() === '') {
          diagnostics.push(emptyDecisionLabel(statement.condition.range, 'if'));
        }
        visitBlock(statement.consequent, context);
        if (statement.alternate) {
          if (statement.alternate.kind === 'Block') visitBlock(statement.alternate, context);
          else visitStatement(statement.alternate, context);
        }
        break;
      }

      case 'WhileStatement':
      case 'RepeatStatement': {
        if (statement.condition.text.trim() === '') {
          diagnostics.push(
            emptyDecisionLabel(
              statement.condition.range,
              statement.kind === 'WhileStatement' ? 'while' : 'until',
            ),
          );
        }
        visitBlock(statement.body, { ...context, loopDepth: context.loopDepth + 1 });
        break;
      }

      case 'ForEachStatement': {
        if (statement.header.text.trim() === '') {
          diagnostics.push(emptyDecisionLabel(statement.header.range, 'for each'));
        }
        visitBlock(statement.body, { ...context, loopDepth: context.loopDepth + 1 });
        break;
      }

      case 'SwitchStatement': {
        if (statement.discriminant.text.trim() === '') {
          diagnostics.push(emptyDecisionLabel(statement.discriminant.range, 'switch'));
        }
        if (statement.cases.length === 0) {
          diagnostics.push({
            code: DiagnosticCode.EmptySwitch,
            severity: 'warning',
            message: 'This `switch` has no cases.',
            range: statement.range,
            suggestion: 'Add at least one `case "…": { … }`.',
          });
        }
        let seenDefault: SwitchCase | null = null;
        for (const branch of statement.cases) {
          recordExplicitId(branch.metadata.id, branch.range);
          if (branch.test === null) {
            if (seenDefault) {
              diagnostics.push({
                code: DiagnosticCode.DuplicateDefaultCase,
                severity: 'error',
                message: 'A `switch` can only have one `default` branch.',
                range: branch.range,
                related: [{ message: 'First `default` here.', range: seenDefault.range }],
              });
            } else {
              seenDefault = branch;
            }
          }
          visitBlock(branch.body, { ...context, switchDepth: context.switchDepth + 1 });
        }
        break;
      }

      case 'ActionStatement':
      case 'StartStatement':
      case 'EndStatement':
      case 'ErrorStatement':
        break;
    }
  };

  function recordExplicitId(id: string | undefined, range: SourceRange): void {
    if (!id) return;
    const existing = explicitIds.get(id);
    if (existing) {
      diagnostics.push({
        code: DiagnosticCode.DuplicateExplicitId,
        severity: 'error',
        message: `The id "${id}" is used more than once.`,
        range,
        suggestion: 'Ids set with `@id(...)` must be unique across the document.',
        related: [{ message: 'First used here.', range: existing }],
        params: { id, line: existing.start.line },
      });
      return;
    }
    explicitIds.set(id, range);
  }

  visitStatements(mainFlow(program), TOP_LEVEL);

  for (const declaration of functions.values()) {
    visitBlock(declaration.body, {
      loopDepth: 0,
      switchDepth: 0,
      functionName: declaration.normalizedName,
    });
  }

  /* --- 3. Multiple explicit starts ---------------------------------- */

  const starts = mainFlow(program).filter((s) => s.kind === 'StartStatement');
  if (starts.length > 1) {
    for (const extra of starts.slice(1)) {
      diagnostics.push({
        code: DiagnosticCode.MultipleStartNodes,
        severity: 'info',
        message: 'The flow already has a start node; this one begins a separate flow.',
        range: extra.range,
        related: [{ message: 'First start here.', range: starts[0]!.range }],
      });
    }
  }

  /* --- 4. Recursion -------------------------------------------------- */

  const recursiveFunctions = findCycles(callGraph, functions);
  for (const name of recursiveFunctions) {
    const declaration = functions.get(name);
    if (!declaration) continue;
    const direct = callGraph.get(name)?.has(name) ?? false;
    diagnostics.push({
      code: direct ? DiagnosticCode.DirectRecursion : DiagnosticCode.IndirectRecursion,
      severity: 'info',
      message: direct
        ? `"${declaration.name.text}" calls itself.`
        : `"${declaration.name.text}" takes part in a cycle of calls.`,
      range: declaration.name.range,
      suggestion: 'Expansion in the diagram stops at the recursion, which is drawn as a back edge.',
      params: { name: declaration.name.text },
    });
  }

  /* --- 5. Reachability of functions ---------------------------------- */

  const reachableFunctions = reachableFrom(callGraph, '');
  for (const [name, declaration] of functions) {
    if (!reachableFunctions.has(name)) {
      diagnostics.push({
        code: DiagnosticCode.UnusedFunction,
        severity: 'info',
        message: `"${declaration.name.text}" is never called, so it does not appear in the main flow.`,
        range: declaration.name.range,
        suggestion: `Add \`call ${declaration.name.text};\` where it belongs.`,
        params: { name: declaration.name.text },
      });
    }
  }

  return {
    functions,
    callGraph,
    callSites,
    recursiveFunctions,
    reachableFunctions,
    explicitIds,
    diagnostics: sortDiagnostics(dedupeDiagnostics(diagnostics)),
  };
}

/* ------------------------------------------------------------------ *
 * Reachability helpers
 * ------------------------------------------------------------------ */

/**
 * Whether control can never continue past this statement.
 *
 * `break` and `continue` count only inside the construct they belong to; the
 * caller supplies that via `context`, which is why this is not a pure function
 * of the statement alone.
 */
export function alwaysTerminates(statement: Statement, context: Context = TOP_LEVEL): boolean {
  switch (statement.kind) {
    case 'EndStatement':
    case 'ReturnStatement':
      return true;
    case 'BreakStatement':
      return context.loopDepth > 0 || context.switchDepth > 0;
    case 'ContinueStatement':
      return context.loopDepth > 0;
    case 'IfStatement': {
      if (!statement.alternate) return false;
      const thenEnds = blockTerminates(statement.consequent, context);
      const elseEnds =
        statement.alternate.kind === 'Block'
          ? blockTerminates(statement.alternate, context)
          : alwaysTerminates(statement.alternate, context);
      return thenEnds && elseEnds;
    }
    case 'SwitchStatement': {
      const hasDefault = statement.cases.some((c) => c.test === null);
      if (!hasDefault || statement.cases.length === 0) return false;
      return statement.cases.every((c) =>
        blockTerminates(c.body, { ...context, switchDepth: context.switchDepth + 1 }),
      );
    }
    // A loop may run zero times, so it never guarantees termination of the
    // surrounding flow. `repeat` runs at least once, but a `break` inside it
    // still lets control continue, so treating it as non-terminating is the
    // conservative choice that avoids false "unreachable code" reports.
    case 'WhileStatement':
    case 'RepeatStatement':
    case 'ForEachStatement':
    case 'ActionStatement':
    case 'StartStatement':
    case 'CallStatement':
    case 'ErrorStatement':
      return false;
  }
}

export function blockTerminates(block: Block, context: Context = TOP_LEVEL): boolean {
  return block.statements.some((s) => alwaysTerminates(s, context));
}

function unreachable(statement: Statement, cause: Statement): Diagnostic {
  const afterEnd = cause.kind === 'EndStatement';
  return {
    code: afterEnd ? DiagnosticCode.StatementAfterEnd : DiagnosticCode.UnreachableCode,
    severity: 'warning',
    message: afterEnd
      ? 'This statement can never run — the flow already ended above.'
      : `This statement can never run — \`${describeTerminator(cause)}\` above always exits.`,
    range: statement.range,
    suggestion: 'Move it above the exit, or remove it.',
    related: [{ message: 'Flow exits here.', range: cause.range }],
    params: { cause: describeTerminator(cause) },
  };
}

function describeTerminator(statement: Statement): string {
  switch (statement.kind) {
    case 'EndStatement':
      return 'end';
    case 'ReturnStatement':
      return 'return';
    case 'BreakStatement':
      return 'break';
    case 'ContinueStatement':
      return 'continue';
    case 'IfStatement':
      return 'if/else';
    case 'SwitchStatement':
      return 'switch';
    default:
      return statement.kind;
  }
}

function emptyDecisionLabel(range: SourceRange, keyword: string): Diagnostic {
  return {
    code: DiagnosticCode.EmptyDecisionLabel,
    severity: 'warning',
    message: `This \`${keyword}\` has no condition text, so the diamond would be blank.`,
    range,
    suggestion: 'Describe the condition, for example `(User is logged in?)`.',
    params: { keyword },
  };
}

/* ------------------------------------------------------------------ *
 * Call-graph algorithms
 * ------------------------------------------------------------------ */

/** Every function that lies on a cycle (Tarjan-style DFS with a colour map). */
function findCycles(
  callGraph: ReadonlyMap<string, ReadonlySet<string>>,
  functions: ReadonlyMap<string, FunctionDeclaration>,
): ReadonlySet<string> {
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const colour = new Map<string, number>();
  const onCycle = new Set<string>();
  const stack: string[] = [];

  const visit = (name: string): void => {
    colour.set(name, GREY);
    stack.push(name);

    for (const callee of callGraph.get(name) ?? []) {
      if (!functions.has(callee)) continue;
      const state = colour.get(callee) ?? WHITE;
      if (state === WHITE) {
        visit(callee);
      } else if (state === GREY) {
        // Everything from `callee` up to the top of the stack is on the cycle.
        const from = stack.lastIndexOf(callee);
        if (from >= 0) for (const member of stack.slice(from)) onCycle.add(member);
      }
    }

    stack.pop();
    colour.set(name, BLACK);
  };

  for (const name of functions.keys()) {
    if ((colour.get(name) ?? WHITE) === WHITE) visit(name);
  }

  return onCycle;
}

/** Breadth-first closure of the call graph from a starting key. */
function reachableFrom(
  callGraph: ReadonlyMap<string, ReadonlySet<string>>,
  root: string,
): ReadonlySet<string> {
  const seen = new Set<string>();
  const queue = [...(callGraph.get(root) ?? [])];
  while (queue.length > 0) {
    const name = queue.shift()!;
    if (seen.has(name)) continue;
    seen.add(name);
    for (const next of callGraph.get(name) ?? []) {
      if (!seen.has(next)) queue.push(next);
    }
  }
  return seen;
}

/* ------------------------------------------------------------------ *
 * "Did you mean…"
 * ------------------------------------------------------------------ */

function suggestFunctionName(
  wanted: string,
  functions: ReadonlyMap<string, FunctionDeclaration>,
): string {
  let best: { name: string; distance: number } | null = null;
  for (const declaration of functions.values()) {
    const distance = editDistance(wanted, declaration.normalizedName);
    if (distance <= Math.max(2, Math.floor(wanted.length / 3))) {
      if (!best || distance < best.distance) {
        best = { name: declaration.name.text, distance };
      }
    }
  }
  if (best) return `Did you mean \`call ${best.name};\`?`;
  if (functions.size === 0) return 'Declare it first with `function name { … }`.';
  return `Declared functions: ${[...functions.values()]
    .slice(0, 5)
    .map((f) => f.name.text)
    .join(', ')}.`;
}

/** Levenshtein distance, capped for short strings — only used for hints. */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(current[j - 1]! + 1, previous[j]! + 1, previous[j - 1]! + cost);
    }
    previous = current;
  }
  return previous[b.length]!;
}
